import { useState, useEffect, useRef } from 'react';

export interface DownloadProgress {
  active: boolean;
  step: 'querying' | 'downloading' | 'zipping';
  current?: number;
  total?: number;
  message: string;
  documentName?: string;
  error?: string;
  sessionId?: string;
}

interface ProgressEvent {
  type: 'start' | 'progress' | 'complete' | 'error';
  step?: 'querying' | 'downloading' | 'zipping';
  current?: number;
  total?: number;
  message?: string;
  documentName?: string;
  sessionId?: string;
  downloadUrl?: string;
}

export function useDownloadProgress() {
  const [progress, setProgress] = useState<DownloadProgress>({
    active: false,
    step: 'querying',
    message: '',
  });
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  const startDownload = (progressEndpoint: string) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setProgress({
      active: true,
      step: 'querying',
      message: 'Verbindung wird aufgebaut...',
    });

    // Get auth token and base URL
    const token = localStorage.getItem('jwt_token');
    const baseURL = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
    
    // Build URL with JWT token as query parameter (EventSource doesn't support custom headers)
    const url = new URL(progressEndpoint, baseURL);
    if (token) {
      url.searchParams.set('token', token);
    }

    const eventSource = new EventSource(url.toString());
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data: ProgressEvent = JSON.parse(event.data);
        
        switch (data.type) {
          case 'start':
            setProgress(prev => ({
              ...prev,
              active: true,
              step: 'querying',
              message: data.message || 'Download gestartet...',
              sessionId: data.sessionId,
              error: undefined,
            }));
            break;

          case 'progress':
            setProgress(prev => ({
              ...prev,
              active: true,
              step: data.step || prev.step,
              current: data.current,
              total: data.total,
              message: data.message || prev.message,
              documentName: data.documentName,
              error: undefined,
            }));
            break;

          case 'complete':
            downloadUrlRef.current = data.downloadUrl || null;
            setProgress(prev => ({
              ...prev,
              active: false,
              message: 'Download abgeschlossen!',
              error: undefined,
            }));
            eventSource.close();
            
            // Auto-download the file
            if (data.downloadUrl) {
              const baseURL = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
              const token = localStorage.getItem('jwt_token');
              const downloadUrl = new URL(data.downloadUrl, baseURL);
              if (token) {
                downloadUrl.searchParams.set('token', token);
              }
              
              const link = document.createElement('a');
              link.href = downloadUrl.toString();
              link.download = '';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
            break;

          case 'error':
            setProgress(prev => ({
              ...prev,
              active: false,
              error: data.message || 'Unbekannter Fehler',
            }));
            eventSource.close();
            break;
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
        setProgress(prev => ({
          ...prev,
          active: false,
          error: 'Fehler beim Verarbeiten der Server-Antwort',
        }));
        eventSource.close();
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
      setProgress(prev => ({
        ...prev,
        active: false,
        error: 'Verbindung zum Server verloren',
      }));
      eventSource.close();
    };

    eventSource.onopen = () => {
      console.log('EventSource connection opened');
    };
  };

  const cancelDownload = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    setProgress({
      active: false,
      step: 'querying',
      message: 'Download abgebrochen',
    });
  };

  const resetProgress = () => {
    setProgress({
      active: false,
      step: 'querying',
      message: '',
    });
    downloadUrlRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    progress,
    startDownload,
    cancelDownload,
    resetProgress,
    downloadUrl: downloadUrlRef.current,
  };
}

// Convenience hooks for specific download types
export function useDocumentsDownloadProgress() {
  const {
    progress,
    startDownload: startDownloadBase,
    cancelDownload,
    resetProgress,
    downloadUrl,
  } = useDownloadProgress();

  const startDownload = () => {
    startDownloadBase('/api/documents/download-all-progress');
  };

  return {
    progress,
    startDownload,
    cancelDownload,
    resetProgress,
    downloadUrl,
  };
}

export function useDogsDownloadProgress() {
  const {
    progress,
    startDownload: startDownloadBase,
    cancelDownload,
    resetProgress,
    downloadUrl,
  } = useDownloadProgress();

  const startDownload = () => {
    startDownloadBase('/api/documents/download-all-members-dogs-progress');
  };

  return {
    progress,
    startDownload,
    cancelDownload,
    resetProgress,
    downloadUrl,
  };
}